import clsx from "clsx";
import dayjs from "dayjs";
import { GenericInfoPopup, Icon, IconName, useDeviceTypes } from "halifax";
import React, { useState, useEffect } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import {
  MODAL_ALERT,
  ModalButtonType,
  ModalCategoryType,
  ModalScreens,
} from "redmond";
import {
  getExperiencesAvailabilityQuery,
  goToExperiencesAvailability,
} from "@capone/common";

import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { PATH_AVAILABILITY, PATH_HOME } from "../../../../utils/paths";
import { ShopErrorModalConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";
import { GO_BACK, SEARCH_AGAIN } from "../../../common/textConstants";

export interface IAvailabilityErrorModalOwnProps {
  skipNoAvailability?: boolean;
}

export interface IAvailabilityErrorModalProps
  extends ShopErrorModalConnectorProps,
    IAvailabilityErrorModalOwnProps,
    RouteComponentProps {}

export const ShopErrorModal = ({
  hasError,
  noAvailability: noAvailabilityRaw,
  locationLabel,
  fromDate,
  untilDate,
  selectedExperienceId,
  selectedDate,
  skipNoAvailability = false,
}: IAvailabilityErrorModalProps) => {
  // When skipNoAvailability is true, the new UnavailableDateModal handles
  // the noAvailability case instead — this modal only handles hasError.
  const noAvailability = skipNoAvailability ? false : noAvailabilityRaw;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();

  const modalEventProperties = {
    type: "experiences_shop_error",
    primary_button: SEARCH_AGAIN,
    secondary_button: GO_BACK,
    screen: ModalScreens.EXPERIENCES_SHOP,
    category: ModalCategoryType.TROUBLE,
    modal_subtitle: noAvailability
      ? textConstants.UNAVAILABLE_DATE_ERROR_SUBTITLE
      : textConstants.SHOP_ERROR_SUBTITLE,
    modal_title: noAvailability
      ? textConstants.UNAVAILABLE_DATE_ERROR_TITLE
      : textConstants.SHOP_ERROR_TITLE,
    funnel: "experiences",
    step: "shop",
    product_id: selectedExperienceId?.value,
    from_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : undefined,
    until_date: untilDate ? dayjs(untilDate).format("YYYY-MM-DD") : undefined,
    selected_date: selectedDate ?? undefined,
  };

  useEffect(() => {
    if (hasError || noAvailability) {
      setIsOpen(true);
      trackEvent({
        eventName: MODAL_ALERT,
        properties: { ...modalEventProperties },
      });
    } else {
      setIsOpen(false);
    }
  }, [hasError, noAvailability]);

  const buttons = noAvailability
    ? [
        // letting user know of no availability so that they can select a different date
        {
          buttonText: textConstants.OK_BUTTON,
          buttonWrapperClassName: clsx(
            "availability-error-modal-button",
            "primary"
          ),
          onClick: () => {
            setIsOpen(false);
            trackEvent({
              eventName: MODAL_ALERT,
              properties: {
                ...modalEventProperties,
                button_choice: ModalButtonType.PRIMARY,
              },
            });
          },
        },
      ]
    : [
        {
          buttonText: textConstants.BACK_TO_RESULTS_BUTTON,
          buttonWrapperClassName: clsx(
            "availability-error-modal-button",
            "primary"
          ),
          onClick: () => {
            setIsOpen(false);
            trackEvent({
              eventName: MODAL_ALERT,
              properties: {
                ...modalEventProperties,
                button_choice: ModalButtonType.PRIMARY,
              },
            });
            // Navigate back preserving category/attraction filters from shopContext
            let categoryTagId: string | null = null;
            try {
              const ctx = localStorage.getItem(
                `shop-context-${selectedExperienceId?.value}`,
              );
              if (ctx) {
                const shopCtx = JSON.parse(ctx);
                if (shopCtx.entryTag?.id) {
                  const kind = shopCtx.entryTag?.kind?.ActivityTagKind === "Attraction" ? "_1" : "_0";
                  categoryTagId = `${shopCtx.entryTag.id}${kind}`;
                }
              }
            } catch {
              // Ignore
            }
            if (categoryTagId && locationLabel) {
              const baseQuery = `${PATH_AVAILABILITY}${getExperiencesAvailabilityQuery(
                locationLabel,
                fromDate,
                untilDate,
              )}`;
              history.push(`${baseQuery}&categoryTagId=${categoryTagId}`);
            } else {
              goToExperiencesAvailability({
                history,
                locationLabel,
                fromDate,
                untilDate,
              });
            }
          },
        },
        {
          buttonText: GO_BACK,
          buttonWrapperClassName: clsx(
            "availability-error-modal-button",
            "secondary"
          ),
          onClick: () => {
            setIsOpen(false);
            trackEvent({
              eventName: MODAL_ALERT,
              properties: {
                ...modalEventProperties,
                button_choice: ModalButtonType.SECONDARY,
              },
            });
            history.push(PATH_HOME);
          },
        },
      ];

  return (
    <GenericInfoPopup
      className="experience-shop-error-modal-root"
      open={isOpen}
      image={<Icon className="error-icon" name={IconName.ErrorState} />}
      title={
        noAvailability
          ? textConstants.UNAVAILABLE_DATE_ERROR_TITLE
          : textConstants.SHOP_ERROR_TITLE
      }
      subtitle={
        noAvailability
          ? textConstants.UNAVAILABLE_DATE_ERROR_SUBTITLE
          : textConstants.SHOP_ERROR_SUBTITLE
      }
      buttons={buttons}
      isMobile={matchesMobile}
    />
  );
};
